import Actions from 'flow/actions';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { List, Map, fromJS, is } from 'immutable';

const initialState = fromJS({
    lobby: {
        meetingAccess: null,
        requireHostPresent: null,
        hostPresent: null,
        meetingJoinBlockedReason: null,
        viewerIsMeetingHost: null,
        members: [],
        pin: null,
        number: null,
        meetingCode: undefined,
        videoStream: null,
        audioStream: null,
        audioActive: true,
        videoActive: false,
        deviceInUse: {
            mic: null,
            video: null,
            audioOutput: null,
        },
        cameraError: null,
        roomNotJoined: {
            roomName: null,
            cause: null,
        },
    },
    activeMeeting: {
        call: {
            userAgent: null,
            callId: null,
            audioReference: null,
            callSession: null,
            callConnectionState: null,
        },
        meetingView: DisplayMode.FULLSCREEN.key,
        meetingAudioActive: true,
        meetingCode: null,
        pin: null,
        number: null,
        chatSessionId: null,
        viewerIsMeetingAdmin: false,
        meetingAccess: null,
        chatHistoryActivated: false,
        meetingType: null,
        members: [],
        isOnHold: false,
        pollingInterval: null,
        isHovering: true,
        isNarrowWindow: false,
        membersListActive: false,
        joinInfoActive: false,
        monitorInterval: null,
        reconnectAttempt: 0,
        reconnectActive: false,
        screenShareMemberName: null,
        chatOpen: false,
        callingMembers: [],
    },
    sender: {
        audioStream: null,
        name: null,
        audioActive: true,
    },
    devices: {
        micAllowed: null,
        videoAllowed: null,
        inUse: {
            mic: null,
            audioOutput: null,
            video: null,
        },
        deviceLists: {
            mics: [],
            audioOutputs: [],
            videos: [],
        },
    },
    error: {
        text: null,
        spinner: false,
        show: false,
        reconnectOnClick: false,
    },
    logs: {
        numberOfMissedLogs: 0,
        logsList: [],
    },
    memberCounts: Map(),
    meetingNumbers: [],
    videoConferenceConfig: null,
});

// eslint-disable-next-line complexity
const meetingReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE:
        case Actions.MEETING_CLEAR_STATE: {
            return initialState;
        }
        case Actions.MEETING_SET_LOBBY_MEETING_CODE: {
            const meetingCode = action && action.meetingCode;
            return state.setIn(['lobby', 'meetingCode'], meetingCode);
        }
        case Actions.MEETING_SET_LOBBY_CHAT_SESSION_ID: {
            const chatSessionId = action && action.chatSessionId;
            return state.setIn(['lobby', 'chatSessionId'], chatSessionId);
        }
        case Actions.MEETING_SET_LOBBY_PIN: {
            const pin = action && action.pin;
            return state.setIn(['lobby', 'pin'], pin);
        }
        case Actions.MEETING_SET_LOBBY_NUMBER: {
            const number = action && action.number;
            return state.setIn(['lobby', 'number'], number);
        }
        case Actions.MEETING_ADD_LOBBY_MEMBERS: {
            const members = action && action.members;
            return state.setIn(['lobby', 'members'], members);
        }
        case Actions.MEETING_SET_LOBBY_VIDEO_STREAM: {
            const videoStream = action && action.videoStream;
            return state.setIn(['lobby', 'videoStream'], videoStream);
        }
        case Actions.MEETING_SET_LOBBY_AUDIO_STREAM: {
            const audioStream = action && action.audioStream;
            return state.setIn(['lobby', 'audioStream'], audioStream);
        }
        case Actions.MEETING_SET_LOBBY_AUDIO_ACTIVE: {
            const audioActive = action && action.audioActive;
            return state.setIn(['lobby', 'audioActive'], audioActive);
        }
        case Actions.MEETING_SET_LOBBY_VIDEO_ACTIVE: {
            const videoActive = action && action.videoActive;
            return state.setIn(['lobby', 'videoActive'], videoActive);
        }
        case Actions.MEETING_SET_LOBBY_MIC_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(['lobby', 'deviceInUse', 'mic'], deviceInUse);
        }
        case Actions.MEETING_SET_LOBBY_VIDEO_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(['lobby', 'deviceInUse', 'video'], deviceInUse);
        }
        case Actions.MEETING_SET_LOBBY_AUDIO_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(
                ['lobby', 'deviceInUse', 'audioOutput'],
                deviceInUse,
            );
        }
        case Actions.MEETING_SET_LOBBY_CAMERA_ERROR: {
            const cameraError = action && action.cameraError;
            return state.setIn(['lobby', 'cameraError'], cameraError);
        }
        case Actions.MEETING_SET_LOBBY_ROOM_NOT_JOINED: {
            const roomName = action && action.roomName;
            const cause = action && action.cause;
            let newState = state.setIn(
                ['lobby', 'roomNotJoined', 'roomName'],
                roomName,
            );
            newState = newState.setIn(
                ['lobby', 'roomNotJoined', 'cause'],
                cause,
            );
            return newState;
        }

        case Actions.MEETING_SET_LOBBY_MEETING_ACCESS: {
            const meetingAccess = action && action.meetingAccess;
            return state.setIn(['lobby', 'meetingAccess'], meetingAccess);
        }
        case Actions.MEETING_SET_LOBBY_REQUIRE_HOST_PRESENT: {
            const requireHostPresent = action && action.requireHostPresent;
            return state.setIn(
                ['lobby', 'requireHostPresent'],
                requireHostPresent,
            );
        }
        case Actions.MEETING_SET_LOBBY_HOST_PRESENT: {
            const hostPresent = action && action.hostPresent;
            return state.setIn(['lobby', 'hostPresent'], hostPresent);
        }
        case Actions.MEETING_SET_LOBBY_MEETING_JOIN_BLOCKED_REASON: {
            const meetingJoinBlockedReason =
                action && action.meetingJoinBlockedReason;
            return state.setIn(
                ['lobby', 'meetingJoinBlockedReason'],
                meetingJoinBlockedReason,
            );
        }
        case Actions.MEETING_SET_LOBBY_VIEWER_IS_MEETING_HOST: {
            const viewerIsMeetingHost = action && action.viewerIsMeetingHost;
            return state.setIn(
                ['lobby', 'viewerIsMeetingHost'],
                viewerIsMeetingHost,
            );
        }
        case Actions.MEETING_SET_JOIN_EXPECTED_TO_BE_HELD: {
            return state.setIn(
                ['activeMeeting', 'joinExpectedToBeHeld'],
                !!(action && action.joinExpectedToBeHeld),
            );
        }

        case Actions.MEETING_SET_ACTIVE_MEETING_MEETING_CODE: {
            const meetingCode = action && action.meetingCode;

            // TODO: Set another state that activates the conference in some way?
            return state.setIn(['activeMeeting', 'meetingCode'], meetingCode);
        }
        case Actions.MEETING_SET_SENDER_NAME: {
            const senderName = action && action.senderName;
            return state.setIn(['sender', 'name'], senderName);
        }
        case Actions.MEETING_SET_SENDER_AUDIO_ACTIVE_STATUS: {
            const audioActive = action && action.audioActive;
            return state.setIn(['sender', 'audioActive'], audioActive);
        }
        case Actions.MEETING_SET_SENDER_AUDIO_STREAM: {
            const audioStream = action && action.audioStream;
            return state.setIn(['sender', 'audioStream'], audioStream);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CALL_ID: {
            const callId = action && action.callId;
            return state.setIn(['activeMeeting', 'call', 'callId'], callId);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_AUDIO_REFERENCE: {
            const audioReference = action && action.audioReference;
            return state.setIn(
                ['activeMeeting', 'call', 'audioReference'],
                audioReference,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CALL_SESSION: {
            const callSession = action && action.callSession;
            return state.setIn(
                ['activeMeeting', 'call', 'callSession'],
                callSession,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CALL_CONNECTION_STATE: {
            const callConnectionState = action && action.connectionState;
            return state.setIn(
                ['activeMeeting', 'call', 'callConnectionState'],
                callConnectionState,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_VIEW: {
            const meetingView = action && action.meetingView;
            return state.setIn(['activeMeeting', 'meetingView'], meetingView);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_PIN: {
            const pin = action && action.pin;
            return state.setIn(['activeMeeting', 'pin'], pin);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_IS_ON_HOLD: {
            const isOnHold = action && action.isOnHold;
            return state.setIn(['activeMeeting', 'isOnHold'], isOnHold);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_POLLING_INTERVAL: {
            const pollingInterval = action && action.pollingInterval;
            return state.setIn(
                ['activeMeeting', 'pollingInterval'],
                pollingInterval,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_IS_HOVERING: {
            const isHovering = action && action.isHovering;
            return state.setIn(['activeMeeting', 'isHovering'], isHovering);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_IS_NARROW_WINDOW: {
            const isNarrowWindow = action && action.isNarrowWindow;
            return state.setIn(
                ['activeMeeting', 'isNarrowWindow'],
                isNarrowWindow,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MEMBER_LIST_ACTIVE: {
            const membersListActive = action && action.membersListActive;
            return state.setIn(
                ['activeMeeting', 'membersListActive'],
                membersListActive,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_JOIN_INFO_ACTIVE: {
            const joinInfoActive = action && action.joinInfoActive;
            return state.setIn(
                ['activeMeeting', 'joinInfoActive'],
                joinInfoActive,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_HOST_CONTROLS_ACTIVE: {
            const hostControlsActive = action && action.hostControlsActive;
            return state.setIn(
                ['activeMeeting', 'hostControlsActive'],
                hostControlsActive,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MEMBERS: {
            const conferenceMembers = action && action.conferenceMembers;
            // Defense-in-depth: keep the existing reference when the members are
            // value-equal (the setActiveMeetingMembers thunk already guards this,
            // but this also covers any direct dispatch) so consumers don't
            // re-render on no-op updates from the members poll.
            const currentMembers = state.getIn(['activeMeeting', 'members']);
            if (
                currentMembers &&
                conferenceMembers &&
                currentMembers.equals(conferenceMembers)
            ) {
                return state;
            }
            return state.setIn(['activeMeeting', 'members'], conferenceMembers);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_NUMBER: {
            const number = action && action.number;
            return state.setIn(['activeMeeting', 'number'], number);
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CHAT_SESSION_ID: {
            const chatSessionId = action && action.chatSessionId;
            return state.setIn(
                ['activeMeeting', 'chatSessionId'],
                chatSessionId,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_VIEWER_IS_MEETING_ADMIN: {
            const viewerIsMeetingAdmin = action && action.viewerIsMeetingAdmin;
            return state.setIn(
                ['activeMeeting', 'viewerIsMeetingAdmin'],
                viewerIsMeetingAdmin,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MEETING_ACCESS: {
            const meetingAccess = action && action.meetingAccess;
            return state.setIn(
                ['activeMeeting', 'meetingAccess'],
                meetingAccess,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CHAT_HISTORY_ACTIVATED: {
            return state.setIn(
                ['activeMeeting', 'chatHistoryActivated'],
                action.chatHistoryActivated,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MEETING_TYPE: {
            return state.setIn(
                ['activeMeeting', 'meetingType'],
                action.meetingType,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_CALL_USER_AGENT: {
            const userAgent = action && action.userAgent;
            return state.setIn(
                ['activeMeeting', 'call', 'userAgent'],
                userAgent,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MONITOR_INTERVAL: {
            const monitorInterval = action && action.monitorInterval;
            return state.setIn(
                ['activeMeeting', 'monitorInterval'],
                monitorInterval,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_RECONNECT_ATTEMPT: {
            const reconnectAttempt = action && action.reconnectAttempt;
            return state.setIn(
                ['activeMeeting', 'reconnectAttempt'],
                reconnectAttempt,
            );
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_RECONNECT_ACTIVE: {
            const reconnectActive = action && action.reconnectActive;
            return state.setIn(
                ['activeMeeting', 'reconnectActive'],
                reconnectActive,
            );
        }
        case Actions.MEETING_SET_ACTIVE_SCREEN_SHARER: {
            const screenShareMemberName =
                action && action.screenShareMemberName;
            return state.setIn(
                ['activeMeeting', 'screenShareMemberName'],
                screenShareMemberName,
            );
        }
        case Actions.MEETING_SET_CHAT_OPEN_STATUS: {
            const chatOpen = action && action.chatOpen;
            return state.setIn(['activeMeeting', 'chatOpen'], chatOpen);
        }
        case Actions.MEETING_SET_MIC_ALLOWED: {
            const micAllowed = action && action.micAllowed;
            return state.setIn(['devices', 'micAllowed'], micAllowed);
        }
        case Actions.MEETING_SET_VIDEO_ALLOWED: {
            const videoAllowed = action && action.videoAllowed;
            return state.setIn(['devices', 'videoAllowed'], videoAllowed);
        }
        case Actions.MEETING_SET_MIC_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(['devices', 'inUse', 'mic'], deviceInUse);
        }
        case Actions.MEETING_SET_AUDIO_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(
                ['devices', 'inUse', 'audioOutput'],
                deviceInUse,
            );
        }
        case Actions.MEETING_SET_VIDEO_DEVICE_IN_USE: {
            const deviceInUse = action && action.deviceInUse;
            return state.setIn(['devices', 'inUse', 'video'], deviceInUse);
        }
        case Actions.MEETING_SET_AUDIO_DEVICE_LIST: {
            const deviceList = action && action.deviceList;
            return state.setIn(
                ['devices', 'deviceLists', 'audioOutputs'],
                deviceList,
            );
        }
        case Actions.MEETING_SET_MIC_DEVICE_LIST: {
            const deviceList = action && action.deviceList;
            return state.setIn(['devices', 'deviceLists', 'mics'], deviceList);
        }
        case Actions.MEETING_SET_VIDEO_DEVICE_LIST: {
            const deviceList = action && action.deviceList;
            return state.setIn(
                ['devices', 'deviceLists', 'videos'],
                deviceList,
            );
        }
        case Actions.MEETING_SET_ERROR: {
            const show = action && action.show;
            const text = action && action.text;
            if (show && !text) {
                console.error('No error text but error set to show');
                return state;
            }
            const spinner = action && action.spinner;
            const reconnectOnClick = action && action.reconnectOnClick;
            let newState = state.setIn(['error', 'show'], show);
            newState = newState.setIn(['error', 'text'], text);
            newState = newState.setIn(['error', 'spinner'], spinner);
            newState = newState.setIn(
                ['error', 'reconnectOnClick'],
                reconnectOnClick,
            );
            return newState;
        }
        case Actions.MEETING_SET_NUMBER_OF_MISSED_LOGS: {
            const numberOfMissedLogs = action && action.numberOfMissedLogs;
            return state.setIn(
                ['logs', 'numberOfMissedLogs'],
                numberOfMissedLogs,
            );
        }
        case Actions.MEETING_SET_NETWORK_ERROR_LOGS: {
            const logs = action && action.logs;
            return state.setIn(['logs', 'logsList'], logs);
        }
        case Actions.MEETING_SET_MEETING_MEMBER_COUNT: {
            const meetingCode = action && action.meetingCode;
            const memberCount = action && action.memberCount;
            return state.setIn(['memberCounts', meetingCode], memberCount);
        }
        case Actions.MEETING_CLEAR_MEMBER_COUNTS: {
            return state.set('memberCounts', Map());
        }
        case Actions.MEETING_SET_MEETING_NUMBERS: {
            const numbersList = action && action.numbersList;
            return state.set('meetingNumbers', numbersList);
        }
        case Actions.MEETING_SET_VIDEO_CONFERENCE_CONFIG: {
            const nextConfig = fromJS(action?.videoConferenceConfig);
            if (is(state.get('videoConferenceConfig'), nextConfig)) {
                return state;
            }
            return state.set('videoConferenceConfig', nextConfig);
        }
        case Actions.MEETING_SET_CALLING_PARTICIPANTS: {
            const callingMembers = action?.callingMembers
                ? List(action?.callingMembers)
                : null;
            return state.setIn(
                ['activeMeeting', 'callingMembers'],
                callingMembers,
            );
        }
        default: {
            return state;
        }
    }
};

export default meetingReducer;
